
import React, { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User, Settings, CreditCard, History, Bell, DollarSign } from "lucide-react";
import SubscriptionStatus from "@/components/subscription/SubscriptionStatus";
import UserBetsHistory from "@/components/software/UserBetsHistory";
import AffiliatePanel from "@/components/affiliate/AffiliatePanel";
import PageInstructions from "@/components/PageInstructions";

const MyAccountPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);

  useEffect(() => {
    if (user?.id) {
      loadProfile();
      loadNotifications();
    }
  }, [user?.id]);

  const loadProfile = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error("Erro ao carregar perfil:", error);
      toast.error("Erro ao carregar perfil");
    }
  };

  const loadNotifications = async () => {
    if (!user?.id) return;

    try {
      console.log("Carregando notificações para usuário:", user.id);
      
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) {
        console.error("Erro na query de notificações:", error);
        throw error;
      }
      
      console.log("Notificações carregadas:", data);
      setNotifications(data || []);
    } catch (error) {
      console.error("Erro ao carregar notificações:", error);
      toast.error("Erro ao carregar notificações");
    }
  };

  const updateProfile = async (updates: any) => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.id);

      if (error) throw error;

      toast.success("Perfil atualizado com sucesso!");
      await loadProfile();
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      toast.error("Erro ao atualizar perfil");
    } finally {
      setIsLoading(false);
    }
  };

  const markNotificationAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) throw error;
      await loadNotifications();
    } catch (error) {
      console.error("Erro ao marcar notificação como lida:", error);
    }
  };

  const getPlanDisplayName = (planValue: string) => {
    const planMap: { [key: string]: string } = {
      'free': 'Gratuito',
      'basic': 'Mensal',
      'premium': 'Trimestral',
      'vip': 'Anual'
    };
    return planMap[planValue] || 'Indefinido';
  };

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground mb-4">
                  Você precisa estar logado para acessar sua conta.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Minha Conta</h1>
            <p className="text-muted-foreground">
              Gerencie suas informações pessoais e configurações
            </p>
          </div>

          <PageInstructions
            title="📖 Como usar Minha Conta"
            steps={[
              "Na aba 'Perfil', veja e edite suas informações pessoais (nome, telefone, WhatsApp)",
              "Na aba 'Plano', veja seu plano atual e gerencie sua assinatura",
              "Na aba 'Apostas', consulte o histórico de jogos gerados pelo Software Turbinado",
              "Na aba 'Avisos', leia as notificações e comunicados do sistema",
              "Na aba 'Afiliado', acesse seu painel de afiliado e acompanhe suas comissões"
            ]}
          />

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="grid w-full grid-cols-6 gap-1 h-auto p-1">
              <TabsTrigger value="profile" className="flex flex-col items-center gap-1 p-2 text-xs">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Perfil</span>
                <span className="sm:hidden">Info</span>
              </TabsTrigger>
              <TabsTrigger value="subscription" className="flex flex-col items-center gap-1 p-2 text-xs">
                <CreditCard className="h-4 w-4" />
                <span className="hidden sm:inline">Plano</span>
                <span className="sm:hidden">Plano</span>
              </TabsTrigger>
              <TabsTrigger value="bets" className="flex flex-col items-center gap-1 p-2 text-xs">
                <History className="h-4 w-4" />
                <span className="hidden sm:inline">Apostas</span>
                <span className="sm:hidden">Jogos</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex flex-col items-center gap-1 p-2 text-xs relative">
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">Avisos</span>
                <span className="sm:hidden">Avisos</span>
                {notifications.filter(n => !n.is_read).length > 0 && (
                  <Badge variant="destructive" className="absolute -top-1 -right-1 h-4 w-4 p-0 text-xs min-w-4 flex items-center justify-center">
                    {notifications.filter(n => !n.is_read).length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="affiliates" className="flex flex-col items-center gap-1 p-2 text-xs">
                <DollarSign className="h-4 w-4" />
                <span className="hidden sm:inline">Afiliados</span>
                <span className="sm:hidden">Afiliados</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex flex-col items-center gap-1 p-2 text-xs">
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Config</span>
                <span className="sm:hidden">Config</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <Card>
                <CardHeader>
                  <CardTitle>Informações Pessoais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Nome</Label>
                      <Input
                        id="firstName"
                        defaultValue={profile?.first_name || ''}
                        onBlur={(e) => updateProfile({ first_name: e.target.value })}
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profile?.email || user.email || ''}
                        disabled
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefone</Label>
                      <Input
                        id="phone"
                        defaultValue={profile?.phone || ''}
                        onBlur={(e) => updateProfile({ phone: e.target.value })}
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Plano Atual</Label>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge variant="secondary" className="text-xs">
                          {getPlanDisplayName(profile?.plan || user?.plan || 'free')}
                        </Badge>
                        {(profile?.is_subscribed || user?.isSubscribed) && (
                          <Badge className="bg-green-100 text-green-800 text-xs">Ativo</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="subscription">
              <SubscriptionStatus />
            </TabsContent>

            <TabsContent value="bets">
              <UserBetsHistory />
            </TabsContent>

            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle>Notificações</CardTitle>
                </CardHeader>
                <CardContent>
                  {notifications.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhuma notificação encontrada.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-3 md:p-4 rounded-lg border ${
                            notification.is_read ? 'bg-gray-50' : 'bg-blue-50 border-blue-200'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <Badge
                                  variant={
                                    notification.type === 'warning' ? 'destructive' :
                                    notification.type === 'success' ? 'default' : 'secondary'
                                  }
                                  className="text-xs"
                                >
                                  {notification.type}
                                </Badge>
                                {!notification.is_read && (
                                  <Badge variant="outline" className="text-xs">Novo</Badge>
                                )}
                              </div>
                              <p className="text-sm break-words">{notification.message}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                {new Date(notification.created_at).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            {!notification.is_read && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => markNotificationAsRead(notification.id)}
                                className="shrink-0 text-xs"
                              >
                                Marcar como lida
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="affiliates">
              <AffiliatePanel />
            </TabsContent>

            <TabsContent value="settings">
              <Card>
                <CardHeader>
                  <CardTitle>Configurações da Conta</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 md:p-4 border rounded-lg">
                      <h4 className="font-medium mb-2 text-sm md:text-base">Período Real da Assinatura</h4>
                      {profile?.subscription_end_date ? (
                        <div className="space-y-2">
                          <p className="text-xs md:text-sm text-gray-600">
                            Sua assinatura está válida até: {new Date(profile.subscription_end_date).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-gray-500">
                            Período de cobrança: {profile.billing_period || 'Não definido'}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs md:text-sm text-gray-600 mb-2 leading-relaxed">
                          {profile?.plan === 'free' ? 
                            'Você está no plano gratuito. Faça upgrade para ter acesso ilimitado.' :
                            'Período de teste gratuito de 5 dias para usar o Software Turbinado.'
                          }
                        </p>
                      )}
                      {profile?.created_at && (
                        <p className="text-xs text-gray-500">
                          Conta criada em: {new Date(profile.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      )}
                    </div>
                    
                    <div className="p-3 md:p-4 border rounded-lg">
                      <h4 className="font-medium mb-2 text-sm md:text-base">Status da Conta</h4>
                      <div className="flex items-center gap-2">
                        <Badge
                          className={`text-xs ${
                            (profile?.is_subscribed || user?.isSubscribed)
                              ? "bg-green-100 text-green-800" 
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {(profile?.is_subscribed || user?.isSubscribed) ? 'Ativa' : 'Teste'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default MyAccountPage;
